import { Eye, EyeOff } from 'lucide-react'
import { useId, useState, type ComponentProps } from 'react'
import { useController, type Control, type FieldPath, type FieldValues } from 'react-hook-form'

import { FieldShell } from '@/components/form/field-shell'
import { MoneyInput } from '@/components/money-input'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

/**
 * Form fields bound to react-hook-form.
 *
 * Each field owns its label, its description, its error message, and the aria wiring between
 * them, so a screen never has to reassemble that by hand — and so no screen accidentally ships a
 * control whose error is visible to a sighted user but silent to a screen reader.
 */

interface FieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>
  name: FieldPath<TFieldValues>
  label: string
  description?: string
  required?: boolean
  className?: string
}

export function TextField<TFieldValues extends FieldValues>({
  control,
  name,
  label,
  description,
  required,
  className,
  ...inputProps
}: FieldProps<TFieldValues> & Omit<ComponentProps<'input'>, 'name' | 'value' | 'onChange' | 'form'>) {
  const id = useId()
  const { field, fieldState } = useController({ control, name })
  const error = fieldState.error?.message

  return (
    <FieldShell id={id} label={label} description={description} required={required} error={error} className={className}>
      <Input
        {...inputProps}
        id={id}
        name={field.name}
        ref={field.ref}
        value={typeof field.value === 'string' ? field.value : ''}
        onChange={field.onChange}
        onBlur={field.onBlur}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : description ? `${id}-description` : undefined}
      />
    </FieldShell>
  )
}

export function PasswordField<TFieldValues extends FieldValues>({
  control,
  name,
  label,
  description,
  required,
  className,
  ...inputProps
}: FieldProps<TFieldValues> & Omit<ComponentProps<'input'>, 'name' | 'value' | 'onChange' | 'type' | 'form'>) {
  const id = useId()
  const [revealed, setRevealed] = useState(false)
  const { field, fieldState } = useController({ control, name })
  const error = fieldState.error?.message

  return (
    <FieldShell id={id} label={label} description={description} required={required} error={error} className={className}>
      <div className="relative">
        <Input
          {...inputProps}
          id={id}
          name={field.name}
          ref={field.ref}
          type={revealed ? 'text' : 'password'}
          value={typeof field.value === 'string' ? field.value : ''}
          onChange={field.onChange}
          onBlur={field.onBlur}
          className="pr-9"
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${id}-error` : description ? `${id}-description` : undefined}
        />
        <button
          type="button"
          onClick={() => setRevealed((current) => !current)}
          className="absolute inset-y-0 right-0 flex w-9 items-center justify-center text-muted transition-colors hover:text-heading"
          aria-label={revealed ? 'Hide password' : 'Show password'}
          // Never submits, never tab-traps: the keyboard path through a sign-in form stays
          // username, password, submit.
          tabIndex={-1}
        >
          {revealed ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
      </div>
    </FieldShell>
  )
}

/**
 * A GHS amount. The form value is an integer count of pesewas or `null` for empty — the schema
 * behind it should validate the integer, never a decimal string.
 */
export function MoneyField<TFieldValues extends FieldValues>({
  control,
  name,
  label,
  description,
  required,
  className,
  currency,
  disabled,
  autoFocus,
}: FieldProps<TFieldValues> & { currency?: string; disabled?: boolean; autoFocus?: boolean }) {
  const id = useId()
  const { field, fieldState } = useController({ control, name })
  const error = fieldState.error?.message

  return (
    <FieldShell id={id} label={label} description={description} required={required} error={error} className={className}>
      <MoneyInput
        id={id}
        name={field.name}
        ref={field.ref}
        currency={currency}
        disabled={disabled}
        autoFocus={autoFocus}
        value={typeof field.value === 'number' ? field.value : null}
        onChange={field.onChange}
        onBlur={field.onBlur}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : description ? `${id}-description` : undefined}
      />
    </FieldShell>
  )
}

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

export function SelectField<TFieldValues extends FieldValues>({
  control,
  name,
  label,
  description,
  required,
  className,
  options,
  placeholder = 'Select…',
  disabled,
}: FieldProps<TFieldValues> & {
  options: SelectOption[]
  placeholder?: string
  disabled?: boolean
}) {
  const id = useId()
  const { field, fieldState } = useController({ control, name })
  const error = fieldState.error?.message

  return (
    <FieldShell id={id} label={label} description={description} required={required} error={error} className={className}>
      <Select
        value={typeof field.value === 'string' ? field.value : undefined}
        onValueChange={field.onChange}
        disabled={disabled}
      >
        <SelectTrigger
          id={id}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${id}-error` : description ? `${id}-description` : undefined}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FieldShell>
  )
}

export function CheckboxField<TFieldValues extends FieldValues>({
  control,
  name,
  label,
  description,
  className,
}: Omit<FieldProps<TFieldValues>, 'required'>) {
  const id = useId()
  const { field, fieldState } = useController({ control, name })
  const error = fieldState.error?.message

  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex items-start gap-2">
        <Checkbox
          id={id}
          name={field.name}
          ref={field.ref}
          checked={field.value === true}
          onCheckedChange={(checked) => field.onChange(checked === true)}
          onBlur={field.onBlur}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${id}-error` : description ? `${id}-description` : undefined}
          className="mt-0.5"
        />
        <div className="space-y-0.5">
          <Label htmlFor={id} className="text-sm font-normal text-body">
            {label}
          </Label>
          {description ? (
            <p id={`${id}-description`} className="text-xs text-muted">
              {description}
            </p>
          ) : null}
        </div>
      </div>
      {error ? (
        <p id={`${id}-error`} className="text-xs text-danger-text">
          {error}
        </p>
      ) : null}
    </div>
  )
}
